/* ============================================================================
 * HELLIX ONDERCONSTRUCTIE CALCULATOR — UI (v3 — meerdere dakvlakken)
 * ----------------------------------------------------------------------------
 *  Top-niveau stappen:
 *    1. Dakvlakken          (overzicht, +nieuw, configureren, verwijderen)
 *    2. Materiaallijst      (geaggregeerd over alle dakvlakken)
 *    3. Winkelwagen
 *
 *  Per dakvlak (sub-wizard):
 *    a. Daktype  b. Dakmaten  c. Paneelmaat  d. Legplan (raster + obstakels)
 * ==========================================================================*/
(function (root, doc) {
  "use strict";

  const FMT_EUR = new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" });
  const CFG = () => root.HELLIX_CALC_CONFIG;
  const ENG = () => root.HellixCalcEngine;
  const VARIANTS = () => root.HELLIX_VARIANT_MAP || {};

  const OBSTACLE_TYPES = {
    dakraam:     { label: "Dakraam",         defaultW: 1200, defaultH: 1000 },
    dakkapel:    { label: "Dakkapel",        defaultW: 2400, defaultH: 1500 },
    schoorsteen: { label: "Schoorsteen",     defaultW:  700, defaultH:  700 },
    ventilatie:  { label: "Ventilatiepijp",  defaultW:  300, defaultH:  300 },
    vrije:       { label: "Vrije uitsparing",defaultW:  800, defaultH:  800 },
  };

  const ICON_H = `
    <svg viewBox="0 0 120 120" width="28" height="28" aria-hidden="true">
      <g transform="translate(10,10)">
        <rect x="0" y="0" width="22" height="100" fill="#E8E4D9"/>
        <rect x="78" y="0" width="22" height="100" fill="#E8E4D9"/>
        <path d="M 22 60 L 78 36 L 78 52 L 22 76 Z" fill="#3FB659"/>
      </g>
    </svg>`;

  /* --- factories --------------------------------------------------------- */
  let _nextRoofId = 1;
  function newRoof(name) {
    return {
      id: _nextRoofId++,
      name: name || `Dakvlak ${_nextRoofId - 1}`,
      roofType: null,
      roofWidthM: null,
      roofHeightM: null,
      panel: null,
      orientation: "portrait",
      rows: 0, cols: 0,
      removed: new Set(),
      obstacles: [],
      selectedObstacleId: null,
      configStep: 1,
      _nextObsId: 1,
    };
  }

  function roofComplete(roof) {
    return !!(roof.roofType && roof.roofWidthM > 0 && roof.roofHeightM > 0 &&
              roof.panel && roof.panel.length > 0 && roof.rows > 0 && roof.cols > 0);
  }

  function effectivePanelCountRoof(roof) {
    return (roof.rows * roof.cols) - mergedBlockedRoof(roof).merged.size;
  }
  function panelOnRoof(roof) {
    return roof.orientation === "portrait"
      ? { w: roof.panel.width, h: roof.panel.length }
      : { w: roof.panel.length, h: roof.panel.width };
  }
  function centerOffset(roof) {
    const p = panelOnRoof(roof);
    const m = CFG().mounting;
    const widthMm  = (roof.roofWidthM  || 0) * 1000;
    const heightMm = (roof.roofHeightM || 0) * 1000;
    const gridW = roof.cols > 0 ? roof.cols * p.w + (roof.cols - 1) * m.panelGapMm : 0;
    const gridH = roof.rows > 0 ? roof.rows * p.h + (roof.rows - 1) * m.rowGapMm  : 0;
    return { x: Math.max(0, (widthMm - gridW) / 2), y: Math.max(0, (heightMm - gridH) / 2) };
  }
  function mergedBlockedRoof(roof) {
    const m = CFG().mounting;
    const off = centerOffset(roof);
    const obsBlocked = ENG().computeBlocked(roof.rows, roof.cols, panelOnRoof(roof), m, roof.obstacles, off);
    const merged = new Set(obsBlocked);
    roof.removed.forEach(k => merged.add(k));
    return { merged, fromObstacles: obsBlocked };
  }

  /* --- top-level markup -------------------------------------------------- */
  function tmpl() {
    return `
      <div class="hx-head">
        <div class="hx-brand">${ICON_H}<span class="hx-name">Hellix · Calculator</span></div>
        <div class="hx-steps" data-stepper></div>
      </div>
      <div data-main></div>
      <div class="hx-actions" data-actions></div>
    `;
  }
  const TOP_STEPS = ["Dakvlakken", "Legplan", "Materiaallijst", "Winkelwagen"];

  function topStepIndex(view) {
    if (view === "overview" || view === "configure") return 1;
    if (view === "legplan")  return 2;
    if (view === "material") return 3;
    if (view === "cart")     return 4;
    return 1;
  }

  function renderStepper(rootEl, state) {
    const wrap = rootEl.querySelector("[data-stepper]");
    const active = topStepIndex(state.view);
    wrap.innerHTML = TOP_STEPS.map((label, i) => {
      const n = i + 1;
      const cls = n === active ? "is-active" : n < active ? "is-done" : "";
      return `<div class="hx-step ${cls}"><span class="hx-step-n">${n}</span>${label}</div>`;
    }).join("");
  }

  /* --- mount: trigger card + open-on-click modal ------------------------- */
  function mount(triggerHost) {
    if (!CFG() || !ENG()) {
      triggerHost.innerHTML = '<p style="color:#E55858;font-family:monospace;">Hellix calculator: config of engine niet geladen.</p>';
      return;
    }
    const cartUrl = triggerHost.dataset.cartUrl || "/cart";
    triggerHost.innerHTML = `
      <div class="hellix-calc-trigger">
        <div>
          <div class="hx-trig-eyebrow">Hellix · Calculator</div>
          <h3 class="hx-trig-title">Bereken in 2 minuten je onderconstructie</h3>
          <p class="hx-trig-sub">Teken je dak, plaats panelen en zie precies welke Hellix-onderdelen je nodig hebt — in één klik aan je winkelwagen toegevoegd.</p>
        </div>
        <button type="button" class="hx-trig-cta" data-hellix-open>Open calculator</button>
      </div>`;
    triggerHost.querySelector("[data-hellix-open]").addEventListener("click", () => openModal(cartUrl));
  }

  function openModal(cartUrl) {
    if (doc.querySelector(".hellix-calc-backdrop")) return; // already open
    const backdrop = doc.createElement("div");
    backdrop.className = "hellix-calc-backdrop";
    backdrop.innerHTML = `
      <div class="hellix-calc-modal" role="dialog" aria-modal="true" aria-label="Hellix calculator">
        <button class="hellix-calc-close" type="button" aria-label="Sluiten">×</button>
        <div class="hellix-calc" data-cart-url="${cartUrl}"></div>
      </div>`;
    doc.body.appendChild(backdrop);
    doc.body.classList.add("hx-modal-open");

    const modalRoot = backdrop.querySelector(".hellix-calc");
    const state = {
      view: "overview",
      roofs: [newRoof("Hoofddak")],
      activeRoofId: null,
      result: null,
    };
    modalRoot.innerHTML = tmpl();
    render(modalRoot, state);

    // iOS keyboard fix: zorg dat focus-input altijd in beeld blijft
    modalRoot.addEventListener("focusin", (ev) => {
      const t = ev.target;
      if (!t) return;
      const tag = t.tagName;
      if (tag !== "INPUT" && tag !== "SELECT" && tag !== "TEXTAREA") return;
      setTimeout(() => {
        try { t.scrollIntoView({ block: "center", behavior: "smooth" }); } catch (_) {}
      }, 280);
    });

    function close() {
      if (!doc.body.contains(backdrop)) return;
      backdrop.remove();
      doc.body.classList.remove("hx-modal-open");
      doc.removeEventListener("keydown", onKey);
    }
    function onKey(e) { if (e.key === "Escape") close(); }
    backdrop.querySelector(".hellix-calc-close").addEventListener("click", close);
    backdrop.addEventListener("click", e => { if (e.target === backdrop) close(); });
    doc.addEventListener("keydown", onKey);
  }

  function render(rootEl, state) {
    renderStepper(rootEl, state);
    const main = rootEl.querySelector("[data-main]");
    const acts = rootEl.querySelector("[data-actions]");
    main.innerHTML = ""; acts.innerHTML = "";

    if (state.view === "overview")  return renderOverview(rootEl, state, main, acts);
    if (state.view === "configure") return renderConfigure(rootEl, state, main, acts);
    if (state.view === "legplan")   return renderLegplan(rootEl, state, main, acts);
    if (state.view === "material")  return renderMaterial(rootEl, state, main, acts);
    if (state.view === "cart")      return renderCart(rootEl, state, main, acts);
  }

  /* ===================================================== VIEW: OVERVIEW == */
  function renderOverview(rootEl, state, main, acts) {
    const completedRoofs = state.roofs.filter(roofComplete);
    const totalPanels = completedRoofs.reduce((s, r) => s + effectivePanelCountRoof(r), 0);
    const stat = totalPanels > 0
      ? `<span class="hx-overview-stat"><b>${state.roofs.length}</b> ${state.roofs.length === 1 ? "dakvlak" : "dakvlakken"}<span style="color:#6E7682;">·</span><b>${totalPanels}</b> ${totalPanels === 1 ? "paneel" : "panelen"}</span>`
      : `<span class="hx-overview-stat"><b>${state.roofs.length}</b> ${state.roofs.length === 1 ? "dakvlak" : "dakvlakken"}</span>`;

    main.innerHTML = `
      <div class="hx-overview-head">
        <h2 class="hx-overview-h">Jouw dakvlakken</h2>
        ${stat}
      </div>
      <p class="hx-overview-sub">
        Voeg elk dak afzonderlijk toe — bv. voorkant, achterkant, of een schuur. De materiaallijst wordt over alle dakvlakken opgeteld.
      </p>
      <div data-roof-list></div>
      <div class="hx-how">
        <h4 class="hx-how-title">Hoe het werkt</h4>
        <div class="hx-how-steps">
          <div class="hx-how-step"><span class="hx-how-step-n">A</span><span class="hx-how-step-label">Daktype<small>pannen · steeldeck · golfplaat</small></span></div>
          <div class="hx-how-step"><span class="hx-how-step-n">B</span><span class="hx-how-step-label">Dakmaten<small>breedte × hoogte</small></span></div>
          <div class="hx-how-step"><span class="hx-how-step-n">C</span><span class="hx-how-step-label">Paneelmaat<small>preset of eigen maat</small></span></div>
          <div class="hx-how-step"><span class="hx-how-step-n">D</span><span class="hx-how-step-label">Legplan<small>panelen + obstakels</small></span></div>
        </div>
      </div>
    `;
    renderRoofList(rootEl, state);

    const anyReady = completedRoofs.length > 0;
    acts.innerHTML = `
      <span></span>
      <button class="hx-btn is-primary" data-go="legplan" ${anyReady ? "" : "disabled"}>
        Bekijk legplan &rarr;
      </button>
    `;
    acts.querySelector("[data-go]").addEventListener("click", () => {
      if (!anyReady) return;
      state.view = "legplan"; render(rootEl, state);
    });
  }

  function renderRoofList(rootEl, state) {
    const wrap = rootEl.querySelector("[data-roof-list]");
    const addCard = `
      <div class="hx-card hx-add-card" data-add-roof>
        <div class="hx-add-icon">+</div>
        <div class="hx-add-label">Dakvlak toevoegen</div>
      </div>`;
    wrap.innerHTML = `<div class="hx-grid">${
      state.roofs.map(roof => roofCard(roof)).join("") + addCard
    }</div>`;
    wrap.querySelectorAll("[data-roof-row]").forEach(card => {
      const id = parseInt(card.dataset.roofRow, 10);
      const nameEl = card.querySelector("[data-edit-name]");
      nameEl.addEventListener("click", e => e.stopPropagation());
      nameEl.addEventListener("input", e => {
        const r = state.roofs.find(x => x.id === id); if (r) r.name = e.target.textContent.trim() || `Dakvlak ${id}`;
      });
      nameEl.addEventListener("keydown", e => {
        if (e.key === "Enter") { e.preventDefault(); nameEl.blur(); }
      });
      card.querySelector("[data-configure]").addEventListener("click", () => {
        state.activeRoofId = id; state.view = "configure"; render(rootEl, state);
      });
      card.querySelector("[data-delete]").addEventListener("click", () => {
        state.roofs = state.roofs.filter(x => x.id !== id);
        if (!state.roofs.length) state.roofs.push(newRoof("Hoofddak"));
        render(rootEl, state);
      });
    });
    const addEl = wrap.querySelector("[data-add-roof]");
    if (addEl) addEl.addEventListener("click", () => {
      const r = newRoof();
      state.roofs.push(r);
      state.activeRoofId = r.id;
      state.view = "configure";
      render(rootEl, state);
    });
  }

  function roofCard(roof) {
    const ok = roofComplete(roof);
    const panels = ok ? effectivePanelCountRoof(roof) : 0;
    const typeLabel = roof.roofType ? CFG().roofTypes[roof.roofType].label : "Nog niet ingesteld";
    const dim = roof.roofWidthM && roof.roofHeightM ? `${roof.roofWidthM} × ${roof.roofHeightM} m` : null;
    const orient = roof.orientation === "landscape" ? "landscape" : "portrait";
    const meta = [typeLabel, dim, ok ? orient : null].filter(Boolean).join(" · ");
    const status = ok
      ? `<span class="hx-roof-status is-complete">${panels} ${panels === 1 ? "paneel" : "panelen"}</span>`
      : `<span class="hx-roof-status is-incomplete">Nog onvolledig</span>`;
    return `
      <div class="hx-card hx-roof-card" data-roof-row="${roof.id}" style="cursor:default;">
        <div class="hx-roof-card-top">
          <div class="hx-roof-name" contenteditable="true" data-edit-name spellcheck="false">${escapeHtml(roof.name)}</div>
          <button class="hx-roof-del" data-delete title="Verwijder">×</button>
        </div>
        <div class="hx-roof-meta">${meta}</div>
        ${status}
        <div class="hx-roof-card-foot">
          <button class="hx-btn ${ok ? '' : 'is-primary'}" data-configure>${ok ? "Bewerken" : "Configureren →"}</button>
        </div>
      </div>`;
  }

  /* ===================================================== VIEW: CONFIGURE = */
  function renderConfigure(rootEl, state, main, acts) {
    const roof = state.roofs.find(r => r.id === state.activeRoofId);
    if (!roof) { state.view = "overview"; return render(rootEl, state); }

    const subSteps = ["Daktype", "Dakmaten", "Paneelmaat", "Legplan"];
    main.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;flex-wrap:wrap;gap:12px;">
        <h2 style="margin:0;">Dakvlak: ${escapeHtml(roof.name)}</h2>
        <button class="hx-btn is-ghost" data-back>&larr; Terug naar dakvlakken</button>
      </div>
      <div style="display:flex;gap:6px;flex-wrap:wrap;margin:6px 0 18px;">
        ${subSteps.map((s, i) => {
          const n = i + 1;
          const cls = n === roof.configStep ? "is-active" : n < roof.configStep ? "is-done" : "";
          return `<div class="hx-step ${cls}" data-sub="${n}"><span class="hx-step-n">${String.fromCharCode(96+n)}</span>${s}</div>`;
        }).join("")}
      </div>
      <div data-cfg-pane></div>
    `;
    main.querySelector("[data-back]").addEventListener("click", () => { state.view = "overview"; render(rootEl, state); });
    main.querySelectorAll("[data-sub]").forEach(el => {
      el.addEventListener("click", () => {
        const n = parseInt(el.dataset.sub, 10);
        if (n < roof.configStep) { roof.configStep = n; render(rootEl, state); }
      });
    });

    const pane = main.querySelector("[data-cfg-pane]");
    if (roof.configStep === 1) renderCfgDaktype(pane, roof);
    if (roof.configStep === 2) renderCfgDakmaten(pane, roof);
    if (roof.configStep === 3) renderCfgPaneelmaat(pane, roof);
    if (roof.configStep === 4) renderCfgLegplan(rootEl, pane, roof, state);

    // navigation actions for sub-wizard
    acts.innerHTML = `
      <button class="hx-btn is-ghost" data-cprev>${roof.configStep === 1 ? "Annuleren" : "Vorige"}</button>
      <button class="hx-btn is-primary" data-cnext>${roof.configStep === 4 ? "Klaar — terug naar dakvlakken" : "Volgende"}</button>
    `;
    acts.querySelector("[data-cprev]").addEventListener("click", () => {
      if (roof.configStep === 1) { state.view = "overview"; render(rootEl, state); }
      else { roof.configStep--; render(rootEl, state); }
    });
    acts.querySelector("[data-cnext]").addEventListener("click", () => onCfgNext(rootEl, state, roof));
  }
  function onCfgNext(rootEl, state, roof) {
    const s = roof.configStep;
    if (s === 1) { if (!roof.roofType) return flash(rootEl, "Kies eerst een daktype."); }
    if (s === 2) { if (!(roof.roofWidthM > 0) || !(roof.roofHeightM > 0)) return flash(rootEl, "Vul dakbreedte en -hoogte in."); }
    if (s === 3) { if (!roof.panel || !roof.panel.length || !roof.panel.width) return flash(rootEl, "Kies (of vul) een paneelmaat."); }
    if (s === 4) { if (effectivePanelCountRoof(roof) < 1) return flash(rootEl, "Plaats minstens 1 paneel."); }
    if (s === 4) { state.view = "overview"; render(rootEl, state); return; }
    roof.configStep++; render(rootEl, state);
  }

  /* --- sub-step a: daktype ----------------------------------------------- */
  function renderCfgDaktype(pane, roof) {
    pane.innerHTML = `<div class="hx-grid">${
      Object.entries(CFG().roofTypes).map(([key, rt]) => `
        <div class="hx-card hx-roof-tile ${roof.roofType === key ? "is-selected" : ""}" data-roof="${key}">
          <div class="hx-roof-tile-icon">${roofTileIcon(key)}</div>
          <div class="hx-card-title">${rt.label}</div>
          <div class="hx-card-sub">${roofTileSub(key)}</div>
        </div>`).join("")
    }</div>`;
    pane.querySelectorAll("[data-roof]").forEach(el => {
      el.addEventListener("click", () => {
        roof.roofType = el.dataset.roof;
        pane.querySelectorAll(".hx-card").forEach(c => c.classList.remove("is-selected"));
        el.classList.add("is-selected");
      });
    });
  }
  function roofTileSub(k) {
    if (k === "pannendak") return "Verstelbare dakhaken, alu rails.";
    if (k === "steeldeck") return "Steeldeck-beugel + trapezeprofiel.";
    if (k === "golfplaat") return "Golfclip + hoekverbinder + alu rails.";
    return "";
  }
  function roofTileIcon(k) {
    const C = '#3FB659';
    if (k === "pannendak") return `
      <svg viewBox="0 0 64 36" aria-hidden="true">
        <path d="M2 28 L32 6 L62 28" fill="none" stroke="${C}" stroke-width="2.5" stroke-linejoin="round"/>
        <path d="M10 22 L14 22 M18 22 L22 22 M26 22 L30 22 M34 22 L38 22 M42 22 L46 22 M50 22 L54 22" stroke="${C}" stroke-width="1.5" stroke-linecap="round" opacity="0.6"/>
        <path d="M8 28 L8 32 L56 32 L56 28" fill="none" stroke="${C}" stroke-width="1.5" opacity="0.5"/>
      </svg>`;
    if (k === "steeldeck") return `
      <svg viewBox="0 0 64 36" aria-hidden="true">
        <path d="M2 28 L32 6 L62 28" fill="none" stroke="${C}" stroke-width="2.5" stroke-linejoin="round"/>
        <path d="M8 26 L11 22 L14 26 L17 22 L20 26 L23 22 L26 26 L29 22 L32 26 L35 22 L38 26 L41 22 L44 26 L47 22 L50 26 L53 22 L56 26" fill="none" stroke="${C}" stroke-width="1.5" stroke-linejoin="miter" opacity="0.7"/>
      </svg>`;
    if (k === "golfplaat") return `
      <svg viewBox="0 0 64 36" aria-hidden="true">
        <path d="M2 28 L32 6 L62 28" fill="none" stroke="${C}" stroke-width="2.5" stroke-linejoin="round"/>
        <path d="M8 24 Q11 20 14 24 T20 24 T26 24 T32 24 T38 24 T44 24 T50 24 T56 24" fill="none" stroke="${C}" stroke-width="1.5" opacity="0.7"/>
      </svg>`;
    return "";
  }

  /* --- sub-step b: dakmaten ---------------------------------------------- */
  function renderCfgDakmaten(pane, roof) {
    pane.innerHTML = `
      <div class="hx-form-row">
        <div class="hx-field"><label>Dakbreedte (m)</label><input type="number" step="0.1" min="1" max="40" data-input="w" value="${roof.roofWidthM || ""}" placeholder="8,0"></div>
        <div class="hx-field"><label>Dakhoogte (m)</label><input type="number" step="0.1" min="1" max="20" data-input="h" value="${roof.roofHeightM || ""}" placeholder="4,0"></div>
      </div>`;
    pane.querySelector('[data-input="w"]').addEventListener("input", e => roof.roofWidthM  = parseFloat(e.target.value) || 0);
    pane.querySelector('[data-input="h"]').addEventListener("input", e => roof.roofHeightM = parseFloat(e.target.value) || 0);
  }

  /* --- sub-step c: paneelmaat -------------------------------------------- */
  function renderCfgPaneelmaat(pane, roof) {
    const presets = CFG().panelPresets.concat([{ id: "p-custom", label: "Eigen maat", length: 0, width: 0 }]);
    const isCustom = roof.panel && !CFG().panelPresets.some(p => p.length === roof.panel.length && p.width === roof.panel.width);
    pane.innerHTML = `
      <div class="hx-grid">${presets.map(p => {
        const sel = roof.panel && (p.id === "p-custom" ? isCustom : (p.length === roof.panel.length && p.width === roof.panel.width));
        const sub = p.id === "p-custom"
          ? "Vul zelf afmetingen in"
          : (p.wp || "Veel voorkomende paneelmaat");
        return `<div class="hx-card ${sel ? "is-selected" : ""}" data-panel="${p.id}" data-l="${p.length}" data-w="${p.width}">
          <div class="hx-card-title">${p.label}</div>
          <div class="hx-card-sub">${sub}</div>
        </div>`;
      }).join("")}</div>
      <div class="hx-form-row" data-custom-row style="display:${isCustom ? "grid" : "none"};margin-top:12px;">
        <div class="hx-field"><label>Lengte (mm)</label><input type="number" min="500" max="2600" data-input="customL" value="${(roof.panel && roof.panel.length) || 1722}"></div>
        <div class="hx-field"><label>Breedte (mm)</label><input type="number" min="500" max="1500" data-input="customW" value="${(roof.panel && roof.panel.width) || 1134}"></div>
      </div>`;
    pane.querySelectorAll("[data-panel]").forEach(el => {
      el.addEventListener("click", () => {
        const id = el.dataset.panel;
        pane.querySelectorAll(".hx-card").forEach(c => c.classList.remove("is-selected"));
        el.classList.add("is-selected");
        pane.querySelector("[data-custom-row]").style.display = (id === "p-custom") ? "grid" : "none";
        if (id === "p-custom") {
          const L = parseInt(pane.querySelector('[data-input="customL"]').value, 10) || 1722;
          const W = parseInt(pane.querySelector('[data-input="customW"]').value, 10) || 1134;
          roof.panel = { length: L, width: W };
        } else {
          roof.panel = { length: parseInt(el.dataset.l, 10), width: parseInt(el.dataset.w, 10) };
        }
      });
    });
    const cL = pane.querySelector('[data-input="customL"]');
    const cW = pane.querySelector('[data-input="customW"]');
    if (cL) cL.addEventListener("input", e => { if (roof.panel) roof.panel.length = parseInt(e.target.value, 10) || 0; });
    if (cW) cW.addEventListener("input", e => { if (roof.panel) roof.panel.width  = parseInt(e.target.value, 10) || 0; });
  }

  /* --- sub-step d: legplan ----------------------------------------------- */
  function renderCfgLegplan(rootEl, pane, roof, state) {
    pane.innerHTML = `
      <div class="hx-canvas-wrap">
        <div class="hx-canvas" data-canvas></div>
        <div class="hx-sidetools">
          <div class="hx-toolgroup">
            <span class="hx-label">Orientatie</span>
            <div class="hx-orient-toggle">
              <button class="hx-btn" data-orient="portrait">Portrait</button>
              <button class="hx-btn" data-orient="landscape">Landscape</button>
            </div>
          </div>
          <div class="hx-toolgroup">
            <span class="hx-label">Rijen</span>
            <button class="hx-btn" data-act="addRow">+ Rij erbij</button>
            <button class="hx-btn" data-act="removeRow">− Rij eraf</button>
          </div>
          <div class="hx-toolgroup">
            <span class="hx-label">Kolommen</span>
            <button class="hx-btn" data-act="addCol">+ Kolom erbij</button>
            <button class="hx-btn" data-act="removeCol">− Kolom eraf</button>
          </div>
          <div class="hx-toolgroup">
            <button class="hx-btn is-ghost" data-act="fillMax">Vul max op dak</button>
            <button class="hx-btn is-ghost" data-act="clearRemoved">Verwijderde herstellen</button>
          </div>
          <div class="hx-toolgroup">
            <span class="hx-label">Obstakels toevoegen</span>
            <button class="hx-btn" data-add-obstacle="dakraam">+ Dakraam</button>
            <button class="hx-btn" data-add-obstacle="dakkapel">+ Dakkapel</button>
            <button class="hx-btn" data-add-obstacle="schoorsteen">+ Schoorsteen</button>
            <button class="hx-btn" data-add-obstacle="ventilatie">+ Ventilatiepijp</button>
            <button class="hx-btn" data-add-obstacle="vrije">+ Vrije uitsparing</button>
            <button class="hx-btn is-ghost" data-act="clearObstacles" style="margin-top:6px;">Alle obstakels weg</button>
          </div>
          <div class="hx-toolgroup" data-obs-list-wrap>
            <span class="hx-label">Geplaatste obstakels — exacte maten</span>
            <div data-obs-list style="display:flex;flex-direction:column;gap:8px;"></div>
          </div>
          <div class="hx-toolgroup">
            <span class="hx-label">Overzicht</span>
            <div class="hx-info"><span>Panelen</span><b data-info="count">0</b></div>
            <div class="hx-info"><span>Raster</span><b data-info="grid">0 × 0</b></div>
            <div class="hx-info"><span>Geblokkeerd</span><b data-info="blocked">0</b></div>
            <div class="hx-info"><span>Daksamenval</span><b data-info="fit">—</b></div>
          </div>
        </div>
      </div>
      <p style="color:var(--hx-ash);font-size:12px;margin-top:10px;">
        Tip: klik op een paneel om hem handmatig te verwijderen. Versleep obstakels op het dak; trek aan de hoek om te schalen; klik op × om weg te halen.
      </p>`;

    // init defaults
    if (roof.rows === 0 && roof.cols === 0) {
      const fit = ENG().maxFit(roof.roofWidthM, roof.roofHeightM, roof.panel, roof.orientation);
      roof.rows = Math.min(fit.rows, 1);
      roof.cols = Math.min(fit.cols, Math.max(1, Math.floor(fit.cols / 2)));
    }
    pane.querySelectorAll("[data-orient]").forEach(b => b.classList.toggle("is-active", b.dataset.orient === roof.orientation));

    bindLegplanTools(pane, roof, rootEl);
    redrawCanvas(rootEl, pane, roof);
  }

  function bindLegplanTools(pane, roof, rootEl) {
    pane.querySelectorAll("[data-orient]").forEach(b => {
      b.addEventListener("click", () => {
        roof.orientation = b.dataset.orient;
        const fit = ENG().maxFit(roof.roofWidthM, roof.roofHeightM, roof.panel, roof.orientation);
        roof.rows = Math.min(roof.rows, fit.rows);
        roof.cols = Math.min(roof.cols, fit.cols);
        pane.querySelectorAll("[data-orient]").forEach(x => x.classList.toggle("is-active", x.dataset.orient === roof.orientation));
        redrawCanvas(rootEl, pane, roof);
      });
    });
    const act = (name, fn) => pane.querySelector(`[data-act="${name}"]`).addEventListener("click", () => { fn(); redrawCanvas(rootEl, pane, roof); });
    act("addRow",    () => { const f = ENG().maxFit(roof.roofWidthM, roof.roofHeightM, roof.panel, roof.orientation); if (roof.rows < f.rows) roof.rows++; if (roof.cols === 0) roof.cols = Math.max(1, f.cols); });
    act("removeRow", () => { if (roof.rows > 0) { roof.rows--; pruneRemoved(roof); } });
    act("addCol",    () => { const f = ENG().maxFit(roof.roofWidthM, roof.roofHeightM, roof.panel, roof.orientation); if (roof.cols < f.cols) roof.cols++; if (roof.rows === 0) roof.rows = 1; });
    act("removeCol", () => { if (roof.cols > 0) { roof.cols--; pruneRemoved(roof); } });
    act("fillMax",   () => { const f = ENG().maxFit(roof.roofWidthM, roof.roofHeightM, roof.panel, roof.orientation); roof.rows = f.rows; roof.cols = f.cols; roof.removed.clear(); });
    act("clearRemoved",   () => roof.removed.clear());
    act("clearObstacles", () => { roof.obstacles = []; roof.selectedObstacleId = null; });
    pane.querySelectorAll("[data-add-obstacle]").forEach(b => {
      b.addEventListener("click", () => {
        const t = b.dataset.addObstacle, def = OBSTACLE_TYPES[t];
        const widthMm = roof.roofWidthM * 1000, heightMm = roof.roofHeightM * 1000;
        // Cap defaults binnen dakvlak (voor smalle/lage daken)
        const initW = Math.min(def.defaultW, Math.max(100, widthMm  - 100));
        const initH = Math.min(def.defaultH, Math.max(100, heightMm - 100));
        const obs = {
          id: roof._nextObsId++, type: t,
          xMm: Math.max(0, (widthMm  - initW) / 2),
          yMm: Math.max(0, (heightMm - initH) / 2),
          wMm: initW, hMm: initH,
        };
        roof.obstacles.push(obs); roof.selectedObstacleId = obs.id;
        redrawCanvas(rootEl, pane, roof);
        // Direct focus op breedte-veld zodat je meteen de juiste maat kunt typen
        setTimeout(() => {
          const inp = pane.querySelector(`input[data-obs-field="wMm"][data-id="${obs.id}"]`);
          if (inp) { try { inp.focus(); inp.select(); } catch(_){} }
        }, 60);
      });
    });
  }
  function pruneRemoved(roof) {
    const ok = new Set();
    roof.removed.forEach(k => { const [r,c] = k.split(",").map(Number); if (r < roof.rows && c < roof.cols) ok.add(k); });
    roof.removed = ok;
  }

  function redrawCanvas(rootEl, pane, roof, opts) {
    opts = opts || {};
    const wrap = pane.querySelector("[data-canvas]");
    const widthMm  = roof.roofWidthM  * 1000;
    const heightMm = roof.roofHeightM * 1000;
    const VB_W = 1200, padding = 30;
    const scale = (VB_W - 2 * padding) / Math.max(widthMm, 1);
    const VB_H = heightMm * scale + 2 * padding;
    const m = CFG().mounting;
    const pOnRoof = panelOnRoof(roof);
    const { merged, fromObstacles } = mergedBlockedRoof(roof);

    const off = centerOffset(roof);
    let svg = `<svg viewBox="0 0 ${VB_W} ${VB_H}" preserveAspectRatio="xMidYMid meet" width="100%" role="img" aria-label="Legplan" style="touch-action:none;">`;
    svg += `<rect class="hx-roof-rect" x="${padding}" y="${padding}" width="${widthMm*scale}" height="${heightMm*scale}" rx="4"/>`;
    for (let mx = 1000; mx < widthMm; mx += 1000)
      svg += `<line class="hx-grid-line" x1="${padding+mx*scale}" y1="${padding}" x2="${padding+mx*scale}" y2="${padding+heightMm*scale}"/>`;
    for (let my = 1000; my < heightMm; my += 1000)
      svg += `<line class="hx-grid-line" x1="${padding}" y1="${padding+my*scale}" x2="${padding+widthMm*scale}" y2="${padding+my*scale}"/>`;
    for (let r = 0; r < roof.rows; r++) {
      for (let c = 0; c < roof.cols; c++) {
        const key = r+","+c;
        const xMm = off.x + c * (pOnRoof.w + m.panelGapMm);
        const yMm = off.y + r * (pOnRoof.h + m.rowGapMm);
        const x = padding + xMm*scale, y = padding + yMm*scale;
        const w = pOnRoof.w*scale, h = pOnRoof.h*scale;
        const blocked = roof.removed.has(key) || fromObstacles.has(key);
        svg += `<rect class="hx-panel ${blocked ? "is-removed" : ""}" data-cell="${key}" x="${x}" y="${y}" width="${w}" height="${h}" rx="2"/>`;
      }
    }
    roof.obstacles.forEach(o => {
      const ox = padding + o.xMm*scale, oy = padding + o.yMm*scale;
      const ow = o.wMm*scale, oh = o.hMm*scale;
      const sel = o.id === roof.selectedObstacleId;
      svg += `<g data-obs="${o.id}">
        <rect x="${ox}" y="${oy}" width="${ow}" height="${oh}" rx="3" fill="rgba(229,88,88,0.18)" stroke="#E55858" stroke-width="${sel?3:2}" stroke-dasharray="6 4" data-obs-body="${o.id}" style="cursor:move;"/>
        <text x="${ox+ow/2}" y="${oy+oh/2}" text-anchor="middle" dominant-baseline="middle" fill="#E8E4D9" font-family="'Bricolage Grotesque',sans-serif" font-size="13" font-weight="900" letter-spacing="2" style="pointer-events:none;text-transform:uppercase;">${OBSTACLE_TYPES[o.type].label}</text>
        ${sel ? `<rect x="${ox+ow-10}" y="${oy+oh-10}" width="14" height="14" fill="#E55858" data-obs-resize="${o.id}" style="cursor:nwse-resize;"/>` : ""}
        <g data-obs-delete="${o.id}" style="cursor:pointer;">
          <circle cx="${ox+ow+8}" cy="${oy-8}" r="10" fill="#0A0C0F" stroke="#E55858" stroke-width="2"/>
          <text x="${ox+ow+8}" y="${oy-4}" text-anchor="middle" fill="#E55858" font-family="'Bricolage Grotesque',sans-serif" font-size="14" font-weight="900" style="pointer-events:none;">×</text>
        </g>
      </g>`;
    });
    svg += `</svg>`;
    wrap.innerHTML = svg;

    wrap.querySelectorAll("[data-cell]").forEach(el => {
      el.addEventListener("click", e => {
        e.stopPropagation();
        const k = el.dataset.cell;
        if (roof.removed.has(k)) roof.removed.delete(k); else roof.removed.add(k);
        redrawCanvas(rootEl, pane, roof);
      });
    });
    wrap.querySelectorAll("[data-obs-delete]").forEach(el => {
      el.addEventListener("pointerdown", e => {
        e.stopPropagation();
        const id = parseInt(el.dataset.obsDelete, 10);
        roof.obstacles = roof.obstacles.filter(o => o.id !== id);
        if (roof.selectedObstacleId === id) roof.selectedObstacleId = null;
        redrawCanvas(rootEl, pane, roof);
      });
    });
    wrap.querySelectorAll("[data-obs-body],[data-obs-resize]").forEach(el => {
      el.addEventListener("pointerdown", e => onObstaclePointer(e, pane, roof, el, scale, padding, widthMm, heightMm, rootEl));
    });

    pane.querySelector('[data-info="count"]').textContent   = (roof.rows*roof.cols) - merged.size;
    pane.querySelector('[data-info="grid"]').textContent    = roof.cols + " × " + roof.rows;
    pane.querySelector('[data-info="blocked"]').textContent = merged.size;
    const fitMax = ENG().maxFit(roof.roofWidthM, roof.roofHeightM, roof.panel, roof.orientation);
    pane.querySelector('[data-info="fit"]').textContent = (roof.rows <= fitMax.rows && roof.cols <= fitMax.cols) ? "Past op dak" : "Te groot!";

    if (!opts.skipList) renderObstacleList(pane, roof, rootEl);
    else syncObstacleListSelection(pane, roof);
  }

  // Updateert alleen de geselecteerde-rand zonder rebuild (focus blijft op input)
  function syncObstacleListSelection(pane, roof) {
    const wrap = pane.querySelector("[data-obs-list]");
    if (!wrap) return;
    wrap.querySelectorAll("[data-obs-row]").forEach(row => {
      const id = parseInt(row.dataset.obsRow, 10);
      row.classList.toggle("is-selected", id === roof.selectedObstacleId);
    });
  }
  // Updateert input values van een specifieke obstakel-rij zonder rebuild
  function syncObstacleInputs(pane, obs) {
    const wrap = pane.querySelector("[data-obs-list]");
    if (!wrap) return;
    ["wMm","hMm","xMm","yMm"].forEach(f => {
      const inp = wrap.querySelector(`input[data-obs-field="${f}"][data-id="${obs.id}"]`);
      if (inp && doc.activeElement !== inp) inp.value = Math.round(obs[f]);
    });
  }

  function renderObstacleList(pane, roof, rootEl) {
    const wrap = pane.querySelector("[data-obs-list]");
    const wrapper = pane.querySelector("[data-obs-list-wrap]");
    if (!wrap) return;
    if (!roof.obstacles.length) {
      wrapper.style.display = "none";
      wrap.innerHTML = "";
      return;
    }
    wrapper.style.display = "";
    const widthMm = roof.roofWidthM * 1000, heightMm = roof.roofHeightM * 1000;
    wrap.innerHTML = roof.obstacles.map(o => {
      const sel = o.id === roof.selectedObstacleId;
      return `
      <div data-obs-row="${o.id}" class="hx-obs-row ${sel ? 'is-selected' : ''}">
        <div class="hx-obs-row-head">
          <span class="hx-obs-row-title">${OBSTACLE_TYPES[o.type].label}</span>
          <button class="hx-obs-row-del" data-obs-row-del="${o.id}" title="Verwijder obstakel">×</button>
        </div>
        <div class="hx-obs-fields">
          <label>B (mm)
            <div class="hx-obs-stepper">
              <button type="button" class="hx-obs-stepper-btn" data-step="-" data-id="${o.id}" data-field="wMm" aria-label="Breedte −">−</button>
              <input type="number" inputmode="numeric" min="100" max="${widthMm}" step="10" value="${Math.round(o.wMm)}" data-obs-field="wMm" data-id="${o.id}">
              <button type="button" class="hx-obs-stepper-btn" data-step="+" data-id="${o.id}" data-field="wMm" aria-label="Breedte +">+</button>
            </div>
          </label>
          <label>H (mm)
            <div class="hx-obs-stepper">
              <button type="button" class="hx-obs-stepper-btn" data-step="-" data-id="${o.id}" data-field="hMm" aria-label="Hoogte −">−</button>
              <input type="number" inputmode="numeric" min="100" max="${heightMm}" step="10" value="${Math.round(o.hMm)}" data-obs-field="hMm" data-id="${o.id}">
              <button type="button" class="hx-obs-stepper-btn" data-step="+" data-id="${o.id}" data-field="hMm" aria-label="Hoogte +">+</button>
            </div>
          </label>
          <label>X v.links
            <div class="hx-obs-stepper">
              <button type="button" class="hx-obs-stepper-btn" data-step="-" data-id="${o.id}" data-field="xMm" aria-label="X −">−</button>
              <input type="number" inputmode="numeric" min="0" max="${widthMm}" step="10" value="${Math.round(o.xMm)}" data-obs-field="xMm" data-id="${o.id}">
              <button type="button" class="hx-obs-stepper-btn" data-step="+" data-id="${o.id}" data-field="xMm" aria-label="X +">+</button>
            </div>
          </label>
          <label>Y v.boven
            <div class="hx-obs-stepper">
              <button type="button" class="hx-obs-stepper-btn" data-step="-" data-id="${o.id}" data-field="yMm" aria-label="Y −">−</button>
              <input type="number" inputmode="numeric" min="0" max="${heightMm}" step="10" value="${Math.round(o.yMm)}" data-obs-field="yMm" data-id="${o.id}">
              <button type="button" class="hx-obs-stepper-btn" data-step="+" data-id="${o.id}" data-field="yMm" aria-label="Y +">+</button>
            </div>
          </label>
        </div>
        <div class="hx-obs-nudge">
          <span class="hx-obs-nudge-label">Verplaats <small>(100 mm)</small></span>
          <div class="hx-obs-nudge-pad">
            <button data-obs-nudge="up"     data-id="${o.id}" aria-label="Omhoog" type="button">↑</button>
            <button data-obs-nudge="left"   data-id="${o.id}" aria-label="Links"  type="button">←</button>
            <button data-obs-nudge="center" data-id="${o.id}" aria-label="Centreer op dak" type="button" title="Centreer op dak">⊕</button>
            <button data-obs-nudge="right"  data-id="${o.id}" aria-label="Rechts" type="button">→</button>
            <button data-obs-nudge="down"   data-id="${o.id}" aria-label="Omlaag" type="button">↓</button>
          </div>
        </div>
      </div>`;
    }).join("");
    const clamp = (v,lo,hi) => Math.max(lo, Math.min(hi, v));
    wrap.querySelectorAll("[data-obs-field]").forEach(inp => {
      inp.addEventListener("input", e => {
        const id = parseInt(inp.dataset.id, 10);
        const obs = roof.obstacles.find(o => o.id === id); if (!obs) return;
        let v = parseFloat(e.target.value); if (!isFinite(v)) return;
        const field = inp.dataset.obsField;
        if (field === "wMm") obs.wMm = clamp(v, 100, widthMm - obs.xMm);
        if (field === "hMm") obs.hMm = clamp(v, 100, heightMm - obs.yMm);
        if (field === "xMm") obs.xMm = clamp(v, 0, widthMm - obs.wMm);
        if (field === "yMm") obs.yMm = clamp(v, 0, heightMm - obs.hMm);
        roof.selectedObstacleId = id;
        // SVG-only redraw: input behoudt focus + cursorpositie
        redrawCanvas(rootEl, pane, roof, { skipList: true });
      });
      inp.addEventListener("focus", () => {
        roof.selectedObstacleId = parseInt(inp.dataset.id, 10);
        syncObstacleListSelection(pane, roof);
        // Klik = volledige tekst selecteren zodat user direct kan overschrijven
        try { inp.select(); } catch(_){}
      });
      inp.addEventListener("blur", () => {
        // Op blur: normaliseer waarde naar geclampte waarde
        const id2 = parseInt(inp.dataset.id, 10);
        const o2 = roof.obstacles.find(o => o.id === id2);
        if (o2) inp.value = Math.round(o2[inp.dataset.obsField]);
      });
    });
    wrap.querySelectorAll(".hx-obs-stepper-btn").forEach(btn => {
      btn.addEventListener("click", e => {
        e.preventDefault();
        const id = parseInt(btn.dataset.id, 10);
        const obs = roof.obstacles.find(o => o.id === id); if (!obs) return;
        const field = btn.dataset.field;
        const dir = btn.dataset.step === "+" ? 1 : -1;
        const step = 10;
        if (field === "wMm") obs.wMm = clamp(obs.wMm + dir * step, 100, widthMm - obs.xMm);
        if (field === "hMm") obs.hMm = clamp(obs.hMm + dir * step, 100, heightMm - obs.yMm);
        if (field === "xMm") obs.xMm = clamp(obs.xMm + dir * step, 0, widthMm - obs.wMm);
        if (field === "yMm") obs.yMm = clamp(obs.yMm + dir * step, 0, heightMm - obs.hMm);
        roof.selectedObstacleId = id;
        syncObstacleInputs(pane, obs);
        redrawCanvas(rootEl, pane, roof, { skipList: true });
      });
    });
    wrap.querySelectorAll("[data-obs-nudge]").forEach(btn => {
      btn.addEventListener("click", e => {
        e.preventDefault();
        const id = parseInt(btn.dataset.id, 10);
        const obs = roof.obstacles.find(o => o.id === id); if (!obs) return;
        roof.selectedObstacleId = id;
        const step = 100;
        const dir = btn.dataset.obsNudge;
        if (dir === "up")    obs.yMm = clamp(obs.yMm - step, 0, heightMm - obs.hMm);
        if (dir === "down")  obs.yMm = clamp(obs.yMm + step, 0, heightMm - obs.hMm);
        if (dir === "left")  obs.xMm = clamp(obs.xMm - step, 0, widthMm  - obs.wMm);
        if (dir === "right") obs.xMm = clamp(obs.xMm + step, 0, widthMm  - obs.wMm);
        if (dir === "center") {
          obs.xMm = Math.max(0, (widthMm  - obs.wMm) / 2);
          obs.yMm = Math.max(0, (heightMm - obs.hMm) / 2);
        }
        syncObstacleInputs(pane, obs);
        redrawCanvas(rootEl, pane, roof, { skipList: true });
      });
    });
    wrap.querySelectorAll("[data-obs-row-del]").forEach(btn => {
      btn.addEventListener("click", () => {
        const id = parseInt(btn.dataset.obsRowDel, 10);
        roof.obstacles = roof.obstacles.filter(o => o.id !== id);
        if (roof.selectedObstacleId === id) roof.selectedObstacleId = null;
        redrawCanvas(rootEl, pane, roof);
      });
    });
    // Bij selectie/nieuw obstakel: scroll de actieve rij in beeld
    if (roof.selectedObstacleId) {
      const sel = wrap.querySelector(`[data-obs-row="${roof.selectedObstacleId}"]`);
      if (sel && typeof sel.scrollIntoView === "function") {
        try { sel.scrollIntoView({ behavior: "smooth", block: "nearest" }); } catch(_){}
      }
    }
  }

  function onObstaclePointer(e, pane, roof, target, scale, padding, widthMm, heightMm, rootEl) {
    e.preventDefault(); e.stopPropagation();
    const svgEl = pane.querySelector("[data-canvas] svg");
    const id = parseInt(target.dataset.obsBody || target.dataset.obsResize, 10);
    const obs = roof.obstacles.find(o => o.id === id); if (!obs) return;
    roof.selectedObstacleId = id;
    const mode = target.dataset.obsResize ? "resize" : "drag";
    if (target.setPointerCapture) { try { target.setPointerCapture(e.pointerId); } catch(_){} }
    // Voorkom dat de modal/page scrollt tijdens slepen (iOS Safari trick)
    const stopTouchMove = (te) => { te.preventDefault(); };
    doc.addEventListener("touchmove", stopTouchMove, { passive: false });
    const mainEl = rootEl.querySelector("[data-main]");
    const prevMainTouch = mainEl ? mainEl.style.touchAction : null;
    const prevMainOverflow = mainEl ? mainEl.style.overflow : null;
    if (mainEl) { mainEl.style.touchAction = "none"; mainEl.style.overflow = "hidden"; }
    doc.body.classList.add("hx-modal-dragging");
    doc.documentElement.classList.add("hx-modal-dragging");
    const pt = svgEl.createSVGPoint();
    function svgCoords(ev) { pt.x = ev.clientX; pt.y = ev.clientY; const ctm = svgEl.getScreenCTM().inverse(); return pt.matrixTransform(ctm); }
    // Convert SVG coords -> mm relative to roof origin
    function toMm(sv) { return { x: (sv.x - padding) / scale, y: (sv.y - padding) / scale }; }
    const startMm = toMm(svgCoords(e));
    // anchor offset = waar in het obstakel je klikt
    const anchor = { x: startMm.x - obs.xMm, y: startMm.y - obs.yMm };
    const startObs = { w: obs.wMm, h: obs.hMm };
    const clamp = (v,lo,hi) => Math.max(lo, Math.min(hi, v));
    function move(ev) {
      const curMm = toMm(svgCoords(ev));
      if (mode === "drag") {
        // Anchor-based: het ankerpunt op het obstakel volgt de cursor — geen 'dode zone' bij dakrand
        obs.xMm = clamp(curMm.x - anchor.x, 0, widthMm  - obs.wMm);
        obs.yMm = clamp(curMm.y - anchor.y, 0, heightMm - obs.hMm);
      } else {
        obs.wMm = clamp(startObs.w + (curMm.x - startMm.x), 100, widthMm  - obs.xMm);
        obs.hMm = clamp(startObs.h + (curMm.y - startMm.y), 100, heightMm - obs.yMm);
      }
      syncObstacleInputs(pane, obs);
      redrawCanvas(rootEl, pane, roof, { skipList: true });
    }
    function up(ev) {
      if (target.releasePointerCapture && ev && ev.pointerId != null) { try { target.releasePointerCapture(ev.pointerId); } catch(_){} }
      doc.removeEventListener("pointermove", move);
      doc.removeEventListener("pointerup", up);
      doc.removeEventListener("pointercancel", up);
      doc.removeEventListener("touchmove", stopTouchMove);
      if (mainEl) {
        mainEl.style.touchAction = prevMainTouch || "";
        mainEl.style.overflow    = prevMainOverflow || "";
      }
      doc.body.classList.remove("hx-modal-dragging");
      doc.documentElement.classList.remove("hx-modal-dragging");
    }
    doc.addEventListener("pointermove", move);
    doc.addEventListener("pointerup", up);
    doc.addEventListener("pointercancel", up);
  }

  /* ===================================================== VIEW: LEGPLAN === */
  function computeRoofResult(roof) {
    const { merged } = mergedBlockedRoof(roof);
    return ENG().calculate({
      roofType: roof.roofType, roofWidthM: roof.roofWidthM, roofHeightM: roof.roofHeightM,
      panel: roof.panel, orientation: roof.orientation, rows: roof.rows, cols: roof.cols,
      blockedCells: merged,
    });
  }

  function renderLegplan(rootEl, state, main, acts) {
    const completed = state.roofs.filter(roofComplete);
    let body = `<h2 style="margin-bottom:10px;">2. Legplan &amp; bevestiging</h2>
      <p style="color:var(--hx-ash);margin:-6px 0 18px;max-width:680px;">
        Zo komt de Hellix-onderconstructie op je dak te liggen — met de rails en bevestigingspunten op de juiste plek per rij.
      </p>
      <div style="display:flex;gap:22px;flex-wrap:wrap;margin-bottom:22px;font-size:12.5px;color:var(--hx-mist);font-family:'JetBrains Mono',monospace;">
        <span style="display:inline-flex;align-items:center;gap:8px;"><span style="display:inline-block;width:26px;height:8px;background:#3FB659;border-radius:1px;"></span>Montagerail</span>
        <span style="display:inline-flex;align-items:center;gap:8px;"><span style="display:inline-block;width:12px;height:12px;background:#E8E4D9;border:2px solid #2D8B43;border-radius:50%;"></span>Bevestiging (dakhaak / beugel / clip)</span>
        <span style="display:inline-flex;align-items:center;gap:8px;"><span style="display:inline-block;width:18px;height:10px;background:rgba(63,182,89,0.14);border:1.5px solid #3FB659;border-radius:2px;"></span>Paneel</span>
        <span style="display:inline-flex;align-items:center;gap:8px;"><span style="display:inline-block;width:18px;height:10px;background:rgba(229,88,88,0.18);border:1.5px dashed #E55858;"></span>Obstakel</span>
      </div>`;

    if (!completed.length) {
      body += `<p style="color:var(--hx-flag);">Configureer eerst een dakvlak om het legplan te zien.</p>`;
    } else {
      completed.forEach(roof => { body += legplanCard(roof, computeRoofResult(roof)); });
    }
    main.innerHTML = body;
    acts.innerHTML = `
      <button class="hx-btn is-ghost" data-back>&larr; Terug naar dakvlakken</button>
      <button class="hx-btn is-primary" data-next ${completed.length ? "" : "disabled"}>Bekijk materiaallijst &rarr;</button>
    `;
    acts.querySelector("[data-back]").addEventListener("click", () => { state.view = "overview"; render(rootEl, state); });
    acts.querySelector("[data-next]").addEventListener("click", () => { state.view = "material"; render(rootEl, state); });
  }

  function legplanCard(roof, result) {
    const widthMm = roof.roofWidthM * 1000;
    const heightMm = roof.roofHeightM * 1000;
    const VB_W = 1100, padding = 24;
    const scale = (VB_W - 2 * padding) / Math.max(widthMm, 1);
    const VB_H = heightMm * scale + 2 * padding;
    const m = CFG().mounting;
    const pOnRoof = panelOnRoof(roof);
    const { merged } = mergedBlockedRoof(roof);

    const off = centerOffset(roof);
    let svg = `<svg viewBox="0 0 ${VB_W} ${VB_H}" preserveAspectRatio="xMidYMid meet" style="display:block;max-width:100%;height:auto;">`;
    svg += `<rect fill="#14181F" stroke="#3D454F" stroke-width="2" x="${padding}" y="${padding}" width="${widthMm*scale}" height="${heightMm*scale}" rx="4"/>`;
    for (let mx = 1000; mx < widthMm; mx += 1000)
      svg += `<line stroke="rgba(126,134,143,0.18)" stroke-width="1" x1="${padding+mx*scale}" y1="${padding}" x2="${padding+mx*scale}" y2="${padding+heightMm*scale}"/>`;
    for (let my = 1000; my < heightMm; my += 1000)
      svg += `<line stroke="rgba(126,134,143,0.18)" stroke-width="1" x1="${padding}" y1="${padding+my*scale}" x2="${padding+widthMm*scale}" y2="${padding+my*scale}"/>`;

    // rails (onder de panelen) — komen uit engine geometry, al gecentreerd
    (result.geometry && result.geometry.rails || []).forEach(rail => {
      const x = padding + rail.xMm * scale;
      const y = padding + rail.yMm * scale;
      const w = rail.wMm * scale;
      const h = Math.max(3, rail.hMm * scale);
      svg += `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="2" fill="#3FB659" opacity="0.9"/>`;
    });

    // panels (boven de rails, semi-transparant) — pas centreer-offset toe
    for (let r = 0; r < roof.rows; r++) {
      for (let c = 0; c < roof.cols; c++) {
        if (merged.has(r + "," + c)) continue;
        const xMm = off.x + c * (pOnRoof.w + m.panelGapMm);
        const yMm = off.y + r * (pOnRoof.h + m.rowGapMm);
        svg += `<rect x="${padding + xMm*scale}" y="${padding + yMm*scale}" width="${pOnRoof.w*scale}" height="${pOnRoof.h*scale}" rx="2" fill="rgba(63,182,89,0.14)" stroke="#3FB659" stroke-width="1.5"/>`;
      }
    }

    // anchors / haken (op de rails)
    (result.geometry && result.geometry.anchors || []).forEach(a => {
      const x = padding + a.xMm * scale;
      const y = padding + a.yMm * scale;
      svg += `<circle cx="${x}" cy="${y}" r="6" fill="#E8E4D9" stroke="#2D8B43" stroke-width="2"/>`;
    });

    // obstacles
    roof.obstacles.forEach(o => {
      const x = padding + o.xMm*scale, y = padding + o.yMm*scale;
      svg += `<rect x="${x}" y="${y}" width="${o.wMm*scale}" height="${o.hMm*scale}" rx="3" fill="rgba(229,88,88,0.18)" stroke="#E55858" stroke-width="1.5" stroke-dasharray="6 4"/>`;
    });

    svg += "</svg>";

    const s = result.summary;
    const railSkus = [...new Set(s.railPiecesPerLine || [])].join(" + ") || "—";
    const stats = `
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(130px,1fr));gap:14px;margin-top:18px;font-family:'JetBrains Mono',monospace;font-size:12px;">
        <div><span style="color:var(--hx-ash);display:block;margin-bottom:3px;">Panelen</span><b style="color:var(--hx-bone);font-size:17px;">${s.totalPanels}</b></div>
        <div><span style="color:var(--hx-ash);display:block;margin-bottom:3px;">Rail-lijnen</span><b style="color:var(--hx-bone);font-size:17px;">${s.totalRailLines}</b></div>
        <div><span style="color:var(--hx-ash);display:block;margin-bottom:3px;">Bevestigingen</span><b style="color:var(--hx-bone);font-size:17px;">${s.totalAnchors}</b></div>
        <div><span style="color:var(--hx-ash);display:block;margin-bottom:3px;">Klemmen</span><b style="color:var(--hx-bone);font-size:17px;">${s.totalEndClamps + s.totalMidClamps}</b></div>
        <div><span style="color:var(--hx-ash);display:block;margin-bottom:3px;">Rail-stuk per lijn</span><b style="color:var(--hx-green);font-size:13px;">${railSkus}</b></div>
      </div>`;

    return `<div style="background:var(--hx-steel);border:1px solid var(--hx-graphite);border-radius:6px;padding:20px;margin-bottom:18px;">
      <h3 style="font-family:'Bricolage Grotesque',sans-serif;font-weight:800;font-size:18px;margin:0 0 14px;color:var(--hx-bone);">${escapeHtml(roof.name)} <span style="color:var(--hx-ash);font-weight:500;font-size:13px;">— ${CFG().roofTypes[roof.roofType].label}, ${roof.roofWidthM}×${roof.roofHeightM} m</span></h3>
      <div style="background:var(--hx-void);border:1px solid var(--hx-graphite);border-radius:4px;padding:10px;">${svg}</div>
      ${stats}
    </div>`;
  }

  /* ===================================================== VIEW: MATERIAL == */
  function renderMaterial(rootEl, state, main, acts) {
    // Aggregate over alle complete dakvlakken
    const totals = new Map();
    const perRoof = [];
    const cfg = CFG();
    state.roofs.forEach(roof => {
      if (!roofComplete(roof)) return;
      const { merged } = mergedBlockedRoof(roof);
      const r = ENG().calculate({
        roofType: roof.roofType, roofWidthM: roof.roofWidthM, roofHeightM: roof.roofHeightM,
        panel: roof.panel, orientation: roof.orientation, rows: roof.rows, cols: roof.cols,
        blockedCells: merged,
      });
      perRoof.push({ name: roof.name, totalPanels: r.summary.totalPanels });
      r.materialList.forEach(it => totals.set(it.sku, (totals.get(it.sku) || 0) + it.qty));
    });
    const materialList = [...totals.entries()].map(([sku, qty]) => ({
      sku, qty, blurb: (cfg.componentBlurbs && cfg.componentBlurbs[sku]) || "",
    }));
    materialList.sort((a, b) => skuOrder(a.sku) - skuOrder(b.sku) || a.sku.localeCompare(b.sku));
    state.result = { materialList, perRoof };

    const vmap = VARIANTS();
    let total = 0; const missing = [];
    const rows = materialList.map(it => {
      const v = vmap[it.sku];
      if (!v) missing.push(it.sku);
      const unit = v ? parseFloat(v.price) : 0;
      const line = unit * it.qty; total += line;
      return `<tr class="${v ? "" : "hx-missing"}">
        <td class="hx-product">
          ${v && v.image ? `<img src="${v.image}" alt="">` : `<div style="width:44px;height:44px;border-radius:4px;background:var(--hx-graphite)"></div>`}
          <div><div class="hx-product-name">${v ? v.title : it.sku}</div>
          <div class="hx-product-blurb">${it.blurb || ""}</div></div>
        </td>
        <td class="num">${it.qty}</td>
        <td class="num">${v ? FMT_EUR.format(unit) : "—"}</td>
        <td class="num">${v ? FMT_EUR.format(line) : "—"}</td>
      </tr>`;
    }).join("");

    main.innerHTML = `
      <h2 style="margin-bottom:10px;">3. Materiaallijst</h2>
      <div style="color:var(--hx-ash);font-family:'JetBrains Mono',monospace;font-size:12px;margin-bottom:14px;">
        ${perRoof.map(r => `<span style="margin-right:14px;">▸ ${escapeHtml(r.name)}: ${r.totalPanels} panelen</span>`).join("") || "Geen dakvlakken berekend."}
      </div>
      <table class="hx-table">
        <thead><tr><th>Product</th><th class="num">Aantal</th><th class="num">Stuks</th><th class="num">Totaal</th></tr></thead>
        <tbody>${rows}</tbody>
        <tfoot><tr class="hx-total-row"><td>Totaal (excl. BTW)</td><td class="num">${perRoof.reduce((s,r)=>s+r.totalPanels,0)} pn</td><td class="num">${materialList.length} regels</td><td class="num">${FMT_EUR.format(total)}</td></tr></tfoot>
      </table>
      ${missing.length ? `<p style="margin-top:12px;color:var(--hx-flag);font-family:monospace;font-size:12px;">Let op: ${missing.length} SKU('s) niet gevonden: ${missing.join(", ")}</p>` : ""}
    `;
    acts.innerHTML = `
      <button class="hx-btn is-ghost" data-back>&larr; Terug naar legplan</button>
      <button class="hx-btn is-primary" data-add-cart ${materialList.length ? "" : "disabled"}>Voeg alles toe aan winkelwagen</button>
    `;
    acts.querySelector("[data-back]").addEventListener("click", () => { state.view = "legplan"; render(rootEl, state); });
    acts.querySelector("[data-add-cart]").addEventListener("click", () => addAllToCart(rootEl, state));
  }
  function skuOrder(sku) {
    if (sku.startsWith("HX-RA") || sku.startsWith("HX-SD-RA")) return 1;
    if (sku.startsWith("HX-DH") || sku.startsWith("HX-SD-BG") ||
        sku.startsWith("HX-GC-CL") || sku.startsWith("HX-SD-TV")) return 2;
    if (sku.startsWith("HX-KL")) return 3;
    if (sku.startsWith("HX-GC-HV")) return 4;
    return 5;
  }

  /* ===================================================== VIEW: CART ====== */
  async function addAllToCart(rootEl, state) {
    const vmap = VARIANTS();
    const items = [], skipped = [];
    state.result.materialList.forEach(item => {
      const v = vmap[item.sku];
      if (!v) { skipped.push(item.sku); return; }
      items.push({ id: parseInt(v.variantId, 10), quantity: item.qty });
    });
    if (!items.length) { flash(rootEl, "Geen producten gekoppeld."); return; }
    const btn = rootEl.querySelector('[data-add-cart]'); btn.disabled = true; btn.textContent = "Toevoegen…";
    try {
      const res = await fetch("/cart/add.js", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ items }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data && data.description || ("cart-add fout (status " + res.status + ")"));
      doc.dispatchEvent(new CustomEvent("cart:refresh"));
      state.view = "cart"; state._cartItems = items; state._cartSkipped = skipped; render(rootEl, state);
    } catch (e) {
      flash(rootEl, "Toevoegen mislukt: " + e.message);
      btn.disabled = false; btn.textContent = "Voeg alles toe aan winkelwagen";
    }
  }
  function renderCart(rootEl, state, main, acts) {
    const vmap = VARIANTS();
    const items = state._cartItems || []; const skipped = state._cartSkipped || [];
    main.innerHTML = `
      <h2 style="margin-bottom:10px;">4. Toegevoegd aan winkelwagen</h2>
      <p style="color:var(--hx-bone);">${items.reduce((s,i)=>s+i.quantity,0)} stuks materiaal toegevoegd.</p>
      ${skipped.length ? `<p style="color:var(--hx-flag);font-family:monospace;font-size:12px;">Overgeslagen: ${skipped.join(", ")}</p>` : ""}
      <ul style="color:var(--hx-ash);font-size:13px;margin-top:12px;padding-left:18px;">
        ${state.result.materialList.map(i => `<li>${i.qty}× ${(vmap[i.sku] && vmap[i.sku].title) || i.sku}</li>`).join("")}
      </ul>
    `;
    acts.innerHTML = `
      <button class="hx-btn is-ghost" data-back>&larr; Terug naar dakvlakken</button>
      <button class="hx-btn is-primary" data-go-cart>Naar winkelwagen</button>
    `;
    acts.querySelector("[data-back]").addEventListener("click", () => { state.view = "overview"; render(rootEl, state); });
    acts.querySelector("[data-go-cart]").addEventListener("click", () => { root.location.href = rootEl.dataset.cartUrl || "/cart"; });
  }

  /* --- utils ------------------------------------------------------------- */
  function flash(rootEl, msg) {
    const bar = doc.createElement("div");
    bar.textContent = msg;
    bar.style.cssText = "position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:#E55858;color:#0A0C0F;padding:10px 16px;border-radius:4px;font-family:'Bricolage Grotesque',sans-serif;font-weight:900;letter-spacing:.1em;text-transform:uppercase;font-size:12px;z-index:9999";
    doc.body.appendChild(bar); setTimeout(() => bar.remove(), 2400);
  }
  function escapeHtml(s) { return String(s).replace(/[&<>"']/g, c => ({"&":"&amp;","<":"&lt;",">":"&gt;","\"":"&quot;","'":"&#39;"}[c])); }

  doc.addEventListener("DOMContentLoaded", () => doc.querySelectorAll("[data-hellix-calc]").forEach(mount));
  root.HellixCalc = { mount };

})(window, document);
